import ArrowOutwardRoundedIcon from "@mui/icons-material/ArrowOutwardRounded";
import { Box, Button, useMediaQuery, useTheme } from "@mui/material";
import { motion, useReducedMotion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import whyUsImg from "../Images/whyUs.png";
import SectionHeading from "../Shared/SectionHeading";
import { DesktopStepper } from "./DesktopStepper";
import { MobileStepper } from "./MobileStepper";

const OurProcess = () => {
  const theme = useTheme();
  const isDesktopStepper = useMediaQuery(theme.breakpoints.up("md"));
  const location = useLocation();
  const navigate = useNavigate();
  const isOurProcessPage = location.pathname === "/our-process";
  const prefersReducedMotion = useReducedMotion();

  return (
    <Box
      id="our-process-section"
      sx={{
        px: { xs: 1.1, sm: 1.8, md: 2.6 },
        py: { xs: 4.2, sm: 4.8, md: 5.8 },
        overflowX: "hidden",
        background: isOurProcessPage
          ? "linear-gradient(180deg, #f8f3ee 0%, #fcfaf7 100%)"
          : "transparent",
      }}
    >
      <SectionHeading
        eyebrow="How We Deliver"
        title="A Process Built for Clarity and Craftsmanship"
        subtitle="Our approach is structured to keep decision-making easy, communication transparent, and execution quality consistent from start to finish."
        align="center"
        titleAs={isOurProcessPage ? "h1" : "h2"}
      />

      <motion.div
        initial={prefersReducedMotion ? false : { opacity: 0, y: 28, scale: 0.985 }}
        whileInView={prefersReducedMotion ? undefined : { opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true, amount: 0.2 }}
        transition={
          prefersReducedMotion
            ? undefined
            : { duration: 0.65, ease: [0.22, 1, 0.36, 1] }
        }
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "1360px",
            mx: "auto",
            borderRadius: { xs: "22px", md: "30px" },
            p: { xs: 1, sm: 1.3, md: 2.4 },
            background:
              "linear-gradient(152deg, rgba(255,255,255,0.86), rgba(255,247,243,0.72))",
            border: "1px solid rgba(128,51,65,0.13)",
            boxShadow: "0 16px 38px rgba(20,8,10,0.1)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                lg: "0.84fr 1.16fr",
              },
              gap: { xs: 1.2, sm: 1.8, md: 2.8, lg: 3.6 },
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                borderRadius: "18px",
                overflow: "hidden",
                minHeight: { xs: 210, sm: 280, md: 340, lg: 460 },
              }}
            >
              <Box
                component="img"
                src={whyUsImg}
                alt="Vista Fusion Studio design process"
                loading="lazy"
                decoding="async"
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  transition: "transform 320ms ease",
                  "&:hover": {
                    transform: "scale(1.04)",
                  },
                }}
                onContextMenu={(event) => event.preventDefault()}
              />
            </Box>

            <Box sx={{ width: "100%", minWidth: 0 }}>
              {isDesktopStepper ? <DesktopStepper /> : <MobileStepper />}
            </Box>
          </Box>
        </Box>
      </motion.div>

      {isOurProcessPage ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4.4 }}>
          <Button
            variant="outlined"
            endIcon={<ArrowOutwardRoundedIcon />}
            onClick={() => navigate("/")}
            sx={{
              px: 2.6,
              py: 1.05,
              borderRadius: "999px",
              textTransform: "none",
              borderColor: "rgba(128,51,65,0.45)",
              color: "#672936",
              fontFamily: '"Manrope", sans-serif',
              fontWeight: 700,
              "&:hover": {
                borderColor: "#803341",
                backgroundColor: "rgba(128,51,65,0.07)",
              },
            }}
          >
            Back to Home
          </Button>
        </Box>
      ) : null}
    </Box>
  );
};

export default OurProcess;
